import React, { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { OfficialAudio, ResolvedAudio } from '@discovr/types';
import { isInstagramAudioUrl } from '@discovr/types';
import { AUDIO_VERIFY_DEBOUNCE_MS, useResolveOfficialAudio } from '@discovr/features';
import { MusicNotesSimple, CheckCircle, SpinnerGap, WarningCircle } from '@phosphor-icons/react';
import { IG_GRADIENT } from '../../campaign-reporting/SoundUsedCell';
import { officialAudioTrackDisplay } from './officialAudioDisplay';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';

const AUDIO_PLACEHOLDER = 'https://www.instagram.com/reels/audio/…';

// Build the OfficialAudio the form submits from a resolved audio (the BE stores
// it as-sent, so carry ids/urls/track).
function officialAudioFromResolved(resolved: ResolvedAudio, fallbackUrl: string): OfficialAudio {
  return {
    audio_ids: [resolved.audio_id],
    audio_urls: [resolved.audio_url || fallbackUrl],
    tracks: [
      {
        audio_id: resolved.audio_id,
        song_name: resolved.track_name,
        artist_name: resolved.artist_name,
        cover_url: resolved.cover_url,
      },
    ],
  };
}

interface CampaignEditAudioFieldProps {
  /** Current form value — the campaign's existing audio, or null when none. */
  value: OfficialAudio | null;
  /** Persist a newly-resolved audio into form state. Never called with null:
   *  clearing isn't supported (the BE setter requires >= 1 audio). */
  onChange: (audio: OfficialAudio) => void;
  /** The field's comment marker, rendered inline with the label. */
  labelExtra?: ReactNode;
}

/**
 * "Campaign audio" editor for the edit form (Instagram only): a persistent add
 * toggle + URL input when none is set, or the track + a Change action when one
 * is. A verified URL writes into form state and ships through the reviewed Save.
 */
export function CampaignEditAudioField({ value, onChange, labelExtra }: CampaignEditAudioFieldProps) {
  // `enabled`: the add toggle (no audio yet). `changing`: replacing an existing one.
  const [enabled, setEnabled] = useState(false);
  const [changing, setChanging] = useState(false);
  const [input, setInput] = useState('');

  const showInput = (!value && enabled) || changing;

  const { status, errorMessage, resolve, reset: resetResolve } = useResolveOfficialAudio((resolved, url) => {
    onChange(officialAudioFromResolved(resolved, url));
    // Committed → collapse both flows; the parent now renders the card.
    setChanging(false);
    setEnabled(false);
    setInput('');
  });

  const resetInput = () => {
    resetResolve(); // invalidate any in-flight verify
    setInput('');
  };

  // Auto-verify a complete, valid URL shortly after it's typed/pasted so the
  // brand never hunts for a button (matches the create flow). The request-id
  // guard (inside the shared hook) drops superseded results.
  useEffect(() => {
    const url = input.trim();
    if (!showInput || status === 'loading' || !isInstagramAudioUrl(url)) return;
    const timer = setTimeout(() => resolve(url), AUDIO_VERIFY_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, showInput]);

  const toggleAdd = (on: boolean) => {
    resetInput();
    setEnabled(on);
  };
  const startChanging = () => {
    resetInput();
    setChanging(true);
  };
  const cancelChanging = () => {
    resetInput();
    setChanging(false);
  };

  const { cover, song, artist } = officialAudioTrackDisplay(value?.tracks?.[0]);

  return (
    <div className="col-span-2 min-w-0 rounded-lg border border-border px-3 py-2.5">
      <div className="flex items-center justify-between gap-1.5">
        <div className="min-w-0">
          <div className="flex min-h-6 items-center gap-1.5 text-body-sm-medium text-foreground">
            <MusicNotesSimple size={14} className="shrink-0 text-foreground" />
            <span>Campaign audio</span>
            <span className="text-caption-regular text-muted-foreground">optional</span>
            {labelExtra}
          </div>
          {/* Subtitle — what the feature is (matches the create flow). Shown
              while there's no committed audio; the track card speaks for itself
              once one is set. */}
          {!value && (
            <div className="text-caption-regular text-muted-foreground">
              Set a common sound all creators use in their videos.
            </div>
          )}
        </div>
        {/* Persistent add toggle — stays visible through the whole add flow so
            the user can switch it back off. Only the add flow has a toggle; an
            existing audio is replaced via its Change button (clearing isn't
            supported). */}
        {!value && (
          <Switch checked={enabled} onCheckedChange={toggleAdd} aria-label="Add campaign audio" className="shrink-0" />
        )}
      </div>

      {/* Existing audio — cover + song + artist + Change. */}
      {value && !changing && (
        <div className="mt-2.5 flex items-center gap-3">
          <span
            className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-muted"
            style={cover ? undefined : { backgroundImage: IG_GRADIENT }}
          >
            {cover ? (
              <img src={cover} alt="" className="size-full object-cover" loading="lazy" />
            ) : (
              <MusicNotesSimple size={18} className="text-white/90" />
            )}
          </span>
          <div className="flex min-w-0 flex-1 flex-col leading-tight">
            <span className="truncate text-body-sm-medium text-foreground">{song}</span>
            <span className="truncate text-caption-regular text-muted-foreground">{artist}</span>
          </div>
          <button
            type="button"
            onClick={startChanging}
            className="shrink-0 rounded-full px-3 py-1 text-body-sm-medium text-foreground hover:bg-muted"
          >
            Change
          </button>
        </div>
      )}

      {/* URL input — add flow (toggle on) or change flow. */}
      {showInput && (
        <>
          <div className="mt-2.5 flex items-center gap-2.5">
            <Input
              type="url"
              inputMode="url"
              aria-label="Instagram audio link"
              aria-invalid={status === 'error'}
              aria-describedby={status === 'error' ? 'campaign-audio-edit-error' : undefined}
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                if (status === 'error') resetResolve();
              }}
              placeholder={AUDIO_PLACEHOLDER}
              disabled={status === 'loading'}
            />
            {status === 'loading' && (
              <span className="inline-flex h-9 shrink-0 items-center gap-1.5 pr-1 text-body-sm-medium text-muted-foreground">
                <SpinnerGap size={16} className="animate-spin" />
                Verifying&hellip;
              </span>
            )}
            {changing && status !== 'loading' && (
              <button
                type="button"
                onClick={cancelChanging}
                className="shrink-0 rounded-full px-3 py-1 text-body-sm-medium text-foreground hover:bg-muted"
              >
                Cancel
              </button>
            )}
          </div>
          {status === 'error' ? (
            <p
              id="campaign-audio-edit-error"
              role="alert"
              className="mt-1.5 flex items-center gap-1 text-caption-regular text-rose-600"
            >
              <WarningCircle size={13} weight="fill" />
              {errorMessage}
            </p>
          ) : (
            <p className="mt-1.5 flex items-center gap-1 text-caption-regular text-muted-foreground">
              <CheckCircle size={13} />
              Paste an Instagram audio link; we verify it before it goes to review.
            </p>
          )}
        </>
      )}
    </div>
  );
}
